// Único lugar onde as duas tabelas de status da OS coexistem de propósito — pra nunca
// mais confundirem. Os ids se sobrepõem entre elas (ex.: 3 = Finalizado no status geral,
// mas 3 = Chegou ao local no andamento), então nenhum dos dois deve ser lido "por analogia"
// com o outro em nenhum componente.

// status_ordem_servico — situação comercial/administrativa (o que a retaguarda enxerga).
export const STATUS_OS_GERAL: { value: number; descricao: string }[] = [
  { value: 1, descricao: 'Pendente' },
  { value: 2, descricao: 'Enviado compras' },
  { value: 3, descricao: 'Finalizado' },
  { value: 4, descricao: 'Agendar' },
  { value: 5, descricao: 'Agendado' },
  { value: 6, descricao: 'Em andamento' },
  { value: 7, descricao: 'Cancelado' },
  { value: 8, descricao: 'Cancelado Contrato' },
  { value: 9, descricao: 'Sem Consultor Operacional' },
  { value: 10, descricao: 'Aceita' },
  { value: 11, descricao: 'Aguardando Fornecedor' },
  { value: 12, descricao: 'Aguardando Confirmação Operador' },
  { value: 13, descricao: 'Em Reajuste' },
]

// status_ordem_servico_andamento — onde o motorista está (o que o Monitoramento acompanha).
export const STATUS_ANDAMENTO: { value: number; descricao: string }[] = [
  { value: 1, descricao: 'Não iniciada' },
  { value: 2, descricao: 'Iniciada' },
  { value: 3, descricao: 'Chegou ao local' },
  { value: 4, descricao: 'Passageiros embarcados' },
  { value: 5, descricao: 'Em trânsito' },
  { value: 6, descricao: 'Finalizado' },
  { value: 7, descricao: 'Parada' },
  { value: 8, descricao: 'Destino' },
]

export const STATUS_ANDAMENTO_PARADA = 7
export const STATUS_ANDAMENTO_DESTINO = 8
export const STATUS_ANDAMENTO_NAO_INICIADA = 1
export const STATUS_ANDAMENTO_FINALIZADO = 6

// De STATUS_OS_GERAL — a Torre confirma/recusa a atribuição feita pelo fornecedor
// só quando a OS está neste status (sprint 9/10, seção 06).
export const STATUS_OS_AGUARDANDO_CONFIRMACAO_OPERADOR = 12

// De STATUS_OS_GERAL — a Torre risca e pinta a linha de vermelho nesses dois
// (farol perde sentido: a OS não vai mais rodar).
export const STATUS_OS_CANCELADO = 7
export const STATUS_OS_CANCELADO_CONTRATO = 8

// Espelho da regra de transição do back só pra alimentar o select do modal — quem valida
// de verdade é a API (400 com mensagem pronta quando a transição não é permitida).
export const TRANSICOES_ANDAMENTO: Record<number, number[]> = {
  1: [2], // Não iniciada -> Iniciada (exige motorista E veículo)
  2: [3], // Iniciada -> Chegou ao local
  3: [4], // Chegou ao local -> Passageiros embarcados
  4: [5], // Passageiros embarcados -> Em trânsito
  5: [7, 8, 6], // Em trânsito -> Parada | Destino | Finalizado
  7: [5, 8], // Parada -> Em trânsito | Destino
  8: [6], // Destino -> Finalizado
  6: [], // Finalizado -> (fim)
}

// statusOsAndamentoId pode vir null da API — tratar sempre como "Não iniciada".
export function descricaoAndamento(id: number | null | undefined): string {
  const found = STATUS_ANDAMENTO.find((s) => s.value === (id ?? STATUS_ANDAMENTO_NAO_INICIADA))
  return found?.descricao ?? 'Não iniciada'
}

// Filtro de POST AgendaFrota/search-paginated/monitoramento. Os 19 campos "de antes" mais
// os 3 novos, todos opcionais — omitir (não mandar a chave) é o que faz a API não filtrar
// por eles, diferente de mandar false/null.
export class SearchMonitoringOsModel {
  public dataInicio: string | null = null
  public dataFinal: string | null = null
  public nomeCliente = ''
  public nomeSubCliente = ''
  public statusIds: number[] | null = null
  public descricaoOrigem = ''
  public descricaoDestino = ''
  public descricaoTipoServico = ''
  public servico = ''
  public descricaoTipoVeiculo = ''
  public descricaoTipoMotorista = ''
  public codigoTg = ''
  public codigoOs = ''

  // Novos na sprint 9/10 — opcionais, omitir não filtra nada.
  public semMotorista?: boolean
  public comOcorrencia?: boolean
  public fornecedorId?: number
}

// ---------------------------------------------------------------------------
// Torre de Monitoramento (contrato novo, tela id 73) — POST Monitoramento/search-paginated.
// Substituiu AgendaFrota/search-paginated/monitoramento. O farol de 4 fases é a peça
// central: cada fase chega com os dois instantes em que muda de cor (alertaEm/criticoEm),
// já resolvidos pelo back a partir de MonitoramentoSettings — não replicar os prazos aqui.
// ---------------------------------------------------------------------------

// As 5 fases fixas do farol (sprint 9/10, leva 5: entrou a fase 1 "Motorista
// notificado" e as antigas 1-4 andaram um número — não ler fases[n] por índice
// fixo nem assumir que fase 1 é "confirmado" em código ou texto antigo).
export const NOME_FASE: Record<number, string> = {
  1: 'Motorista notificado',
  2: 'Motorista confirmado',
  3: 'Motorista no local',
  4: 'Passageiro embarcado',
  5: 'Serviço finalizado',
}

// Status de uma bolinha — não confundir com StatusServico (a linha/os cards).
export type StatusFase = 'pendente' | 'alerta' | 'critico' | 'confirmada'

// Status da linha e dos cards — a agregação é a fase mais grave AINDA EM ABERTO;
// fase confirmada já passou e não pesa. no_show e finalizado ganham de qualquer cor.
export type StatusServico = 'no_prazo' | 'alerta' | 'critico' | 'no_show' | 'finalizado'

export interface FaseFarolVm {
  numero: number
  nome: string
  status: StatusFase
  referencia: string | null
  alertaEm: string | null
  criticoEm: string | null
  confirmadoEm: string | null
  confirmadoPorId: number | null
  confirmadoPor: string | null
  // Confirmada por dedução (motorista sem sinal registrou tudo de uma vez) — sem
  // horário e sem autor. Mostrar a bolinha verde e omitir "registrado por".
  inferida: boolean
  // De quem cobrar quando a fase está aberta (sprint 9/10). A cor não muda — só a
  // atribuição: 'torre' só ocorre na fase 1 "Motorista notificado" ainda aberta (o
  // problema é nosso, não do motorista); 'motorista' em qualquer fase de 2 a 5 ainda
  // aberta; null quando a fase já foi cumprida.
  pendenciaDe: 'torre' | 'motorista' | null
}

export interface FarolVm {
  faseAtual: number // primeira fase não confirmada; 6 = todas confirmadas (5 fases desde a leva 5)
  statusServico: StatusServico
  fases: FaseFarolVm[]
}

// Cor de uma fase é função pura de (fase, agora) — é o que deixa a tela repintar
// sozinha a cada ~10s sem nenhuma requisição. Usar sempre o relógio do servidor
// (servidorAgora do payload), nunca o relógio local puro.
export function corDaFase(fase: FaseFarolVm, agora: Date): StatusFase {
  if (fase.status === 'confirmada') return 'confirmada'
  if (fase.criticoEm && agora >= new Date(fase.criticoEm)) return 'critico'
  if (fase.alertaEm && agora >= new Date(fase.alertaEm)) return 'alerta'
  return 'pendente'
}

// tipo/tipoOcorrenciaId saíram do contrato na sprint 9/10 — não existem mais dois
// no-shows (motorista some vira troca de motorista, não no-show). Não reintroduzir.
export interface NoShowVm {
  id: string
  detalhe: string | null
  registradoEm: string // quando ACONTECEU, não quando foi lançado
  registradoPor: string
}

// Corpo do POST/DELETE Monitoramento/{osId}/no-show — os três campos são opcionais;
// omitir dataOcorrido/horaOcorrido faz o back carimbar a hora atual.
export interface NoShowBody {
  dataOcorrido?: string // dd/MM/yyyy
  horaOcorrido?: string // HH:mm
  observacao?: string
}

// POST Monitoramento/{osId}/chegada-casa — vazio = hoje / agora
export interface ChegadaCasaBody {
  dataOcorrido?: string // dd/MM/yyyy
  horaOcorrido?: string // HH:mm
}

// Carimbo de POST Monitoramento/{osId}/confirmar-envio — quem registrou ter mandado
// a OS pro motorista (o sistema não dispara WhatsApp, quem manda é o operador pelo
// link do mensagem-motorista; isto é só o registro). Null enquanto ninguém confirmou.
// Clicar de novo atualiza o carimbo — reenvio é reenvio, o que interessa é o último.
export interface EnvioVm {
  em: string
  porId: number
  por: string
}

// Corpo do POST Monitoramento/{osId}/atribuir — mande motorista, veículo ou os
// dois. agendaMotoristaId/agendaVeiculoId são o motorista.agendaId/veiculo.agendaId
// que a própria linha devolveu; o back decide create (null) vs edit (preenchido) —
// a Torre não decide mais isso, ver seção 06 do doc de sprint 9/10. dataInicio
// vazio/omitido = usa a janela da própria OS.
export interface AtribuirBody {
  motoristaId?: number
  veiculoId?: string
  agendaMotoristaId?: string | null
  agendaVeiculoId?: string | null
  dataInicio?: string | null
}

// Linha do histórico de alterações da OS (retaguarda, Torre ou portal do
// fornecedor) — de/para já vêm legíveis (nome/placa/valor), não ids. Lista
// completa do mais recente pro mais antigo; vazia quando nada mudou.
export interface AlteracaoVm {
  // Já visto em snake_case (data_inicio, hora_final...) e o back pode migrar pro
  // camelCase que o resto do payload usa (dataInicio, horaFinal...) sem aviso —
  // nunca comparar/indexar por este valor cru, usar sempre normalizarCampoAlteracao.
  campo: 'motorista' | 'veiculo' | 'data_inicio' | 'hora_inicio' | 'data_final' | 'hora_final' | 'valor_compra' | 'valor_proposta' | 'valor_tarifario' | string
  de: string | null
  para: string | null
  em: string
  porId: number
  por: string
}

// Forma canônica (camelCase) de AlteracaoVm.campo, pra comparar/rotular sem se
// importar com qual convenção o back mandou desta vez.
export function normalizarCampoAlteracao(campo: string): string {
  return campo.replace(/_([a-z])/g, (_, letra) => letra.toUpperCase())
}

export interface EnderecoMonitoramentoVm {
  id: number
  resumo: string
  enderecoCompleto: string
  cidade: string
  // Nome por extenso ("São Paulo") — nunca truncar para sigla.
  estado: string
}

// Janela do agendamento (AgendaMotorista/AgendaVeiculo) — vem null junto com o
// agendaId correspondente (ninguém alocado ainda). É o que o edit espera de volta
// no corpo; nunca derivar de LinhaMonitoramentoVm.dataFinal/horaFinal (o fim
// contratado da OS, cru) — uma troca de pessoa reescreveria a janela sem pedido.
export interface JanelaAgendamentoVm {
  dataInicio: string
  horaInicio: string
  dataFinal: string
  horaFinal: string
}

export interface MotoristaMonitoramentoVm {
  id: number
  nome: string
  cpf: string
  telefone: string
  // Id do registro em AgendaMotorista — null = ninguém alocado (create), preenchido
  // = já existe alocação a trocar (edit). Confirmado com o back em 28/08/2026.
  agendaId: string | null
  janela: JanelaAgendamentoVm | null
}

export interface VeiculoMonitoramentoVm {
  id: number | null
  categoria: string | null
  modelo: string | null
  placa: string | null
  // Id do registro em AgendaVeiculo — mesma regra do agendaId do motorista.
  agendaId: string | null
  janela: JanelaAgendamentoVm | null
}

export interface FornecedorMonitoramentoVm {
  id: number | null
  tipo: 'frota_propria' | 'parceiro'
  nome: string
}

// Vem null (não objeto vazio) quando o passageiro foi cadastrado sem endereço —
// cerca de 40% da base. Checar o objeto inteiro, não campo a campo.
export interface EmbarqueDesembarqueVm {
  tipo: 'HOTEL' | 'AEROPORTO' | 'ENDERECO'
  enderecoCompleto: string | null
  cidade: string | null
  aeroportoSigla: string | null
}

export interface PassageiroMonitoramentoVm {
  id: string
  nome: string
  contato: string
  embarque: EmbarqueDesembarqueVm | null
  desembarque: EmbarqueDesembarqueVm | null
}

// idioma só existe como mono/bilíngue/null no sistema — o mockup mostra "Alemão"
// por engano; o back nunca guardou idioma nomeado.
export function descricaoIdioma(idioma: 'mono' | 'bilingue' | null): string {
  if (idioma === 'mono') return 'Mono'
  if (idioma === 'bilingue') return 'Bilíngue'
  return '—'
}

export interface LinhaMonitoramentoVm {
  codigoOs: string
  // Guid do ContratoItem — a Torre abre "Editar OS" com este id, não o codigoOs.
  // Nunca vem null: a consulta faz INNER join com o item, OS sem pai não aparece
  // na listagem. Confirmado com o back em 03/09/2026.
  contratoItemId: string
  codigoTg: string
  data: string
  // Calculado pelo back, não existe no banco: início menos 30min (era 15min antes
  // da sprint 9/10 — mudança de regra da operação, não bug). É a referência da
  // fase 2 do farol (alertaEm/criticoEm da fase 2 se movem junto).
  horaApresentacao: string | null
  horaInicio: string | null
  // Estimativa de rota de verdade (Google, considerando trânsito do horário de
  // início) desde a sprint 9/10 — ver origemPrevisao. Null/indisponível é comum:
  // só ~15% das OS têm endereço de rua nas duas pontas hoje. Não confundir com
  // horaFinal (fim contratado, cru).
  horaTerminoPrevisto: string | null
  duracaoPrevistaMinutos: number | null
  distanciaPrevistaKm: number | null
  // google = rota real; indisponível = faltou endereço de rua numa ponta (definitivo,
  // não avisa mais tarde); null = ainda não calculada (temporário, não avisar nada,
  // some sozinho); simulado = número estimado no servidor sem Google, só existe no qa.
  origemPrevisao: 'google' | 'indisponivel' | 'simulado' | null
  // Fim contratado da OS, cru — 58min após o início em 8.680 das 10.315 OS (default
  // de cadastro, não previsão de quem montou o serviço). Serve pra exibir e pra
  // popular a janela na criação de um agendamento novo; nunca usar pra farol/atraso
  // (é por isso que a fase 4 usa horaTerminoPrevisto/referencia, não este campo).
  dataFinal: string
  horaFinal: string | null
  // Carimbo de quando o motorista finalizou tudo no app — já era gravado no
  // histórico de andamento, só passou a vir como campo próprio na linha. Nulo
  // enquanto o serviço não termina. NUNCA sobrescreve horaFinal/dataFinal (o
  // combinado com o cliente não pode desaparecer) — os dois convivem: um serviço
  // contratado 05:30-06:30 pode finalizar de verdade às 14:31 e ambos ficam.
  dataTerminoReal: string | null
  horaTerminoReal: string | null
  nomeEvento: string

  cliente: string
  clienteId: number
  subCliente: string | null
  subClienteId: number | null
  consultora: { id: number; nome: string } | null

  origem: EnderecoMonitoramentoVm
  destino: EnderecoMonitoramentoVm

  tipoServico: string
  servico: string
  idioma: 'mono' | 'bilingue' | null

  veiculo: VeiculoMonitoramentoVm
  fornecedor: FornecedorMonitoramentoVm
  motorista: MotoristaMonitoramentoVm | null

  passageiros: PassageiroMonitoramentoVm[]
  totalPassageiros: number

  farol: FarolVm
  noShow: NoShowVm | null
  // Null enquanto ninguém confirmou ter mandado a OS pro motorista. Ver EnvioVm.
  envio: EnvioVm | null
  temOcorrencia: boolean

  statusId: number
  descricaoStatus: string
  statusOsAndamentoId: number | null
  statusOsAndamento: string

  observacao: string | null

  // Sprint 9/10, seção 06 — falta motorista OU veículo (ou os dois). Campo
  // separado de propósito, NÃO é uma cor do farol: uma OS sem motorista a 20min
  // do embarque tem que continuar aparecendo vermelha se estiver atrasada, não
  // virar verde só porque "sem alocação" é sempre bom sinal em outro contexto.
  semAlocacao: boolean

  // Sprint 9/10, seção 07 — o que mudou depois que a OS nasceu, mais recente
  // primeiro. Cobre retaguarda, Torre e portal do fornecedor.
  alteracoes: AlteracaoVm[]
  // true = motorista alocado nunca rodou na TG antes desta OS; null = ninguém
  // alocado ainda (a pergunta não tem resposta). avisoMotorista vem pronto pro
  // operador copiar/mostrar — hoje o envio do arquivo de orientação é manual.
  primeiroServicoMotorista: boolean | null
  avisoMotorista: string | null
}

export interface ResumoMonitoramentoVm {
  servicosHoje: number
  ativos: number
  finalizados: number
  noPrazo: number
  alerta: number
  critico: number
  noShow: number
  semMotorista: number
  // Sprint 9/10 — motorista OU veículo faltando; maior que semMotorista, que só
  // olhava motorista (a fila de "tem motorista, falta carro" ficava invisível).
  semAlocacao: number
}

export interface MonitoramentoSearchResponseVm {
  resumo: ResumoMonitoramentoVm
  servidorAgora: string
  itens: LinhaMonitoramentoVm[]
}

// Filtro de POST Monitoramento/search-paginated. Omitir a chave (não mandar
// false/null/'') é o que faz a API não filtrar por ela.
export class SearchMonitoramentoModel {
  public dataInicio: string | null = null // vazio = hoje
  public dataFinal: string | null = null // vazio = igual ao início; teto de 31 dias
  public aba: 'ativos' | 'finalizados' = 'ativos'
  public busca = ''
  // Casa por prefixo: "TG26-5269" traz o evento todo, "TG26-5269-0011" só a OS.
  // Separado da busca livre (que também casa cliente/passageiro). Quando vem, vence
  // o "vazio = hoje" das datas — a OS aparece sem saber de que dia ela é (janela de
  // um ano pra trás e pra frente, pro prefixo solto não varrer a base).
  public codigoOs?: string
  // Vence `motorista` (nome parcial, sem acento) quando os dois vêm.
  public motoristaId?: number
  public statusFarol: StatusServico | null = null
  public semMotorista?: boolean
  public semAlocacao?: boolean // novo na sprint 9/10 — distinto de semMotorista, ver ResumoMonitoramentoVm
  public comOcorrencia?: boolean
  public tipoFornecedor?: 'frota_propria' | 'parceiro'
  public clienteId?: number
  public fornecedorId?: number
  public consultorOperacionalId?: number

  // Novos na sprint 9/10. Campo desconhecido não dá erro no back — cai em "data".
  public ordenarPor?: OrdenacaoMonitoramento // padrão: "data"
  public direcao?: 'asc' | 'desc' // padrão: "asc"
}

export type OrdenacaoMonitoramento =
  | 'data'
  | 'hora'
  | 'horaApresentacao'
  | 'cliente'
  | 'subCliente'
  | 'motorista'
  | 'veiculo'
  | 'fornecedor'
  | 'statusFarol'
  | 'faseAtual'
  | 'codigoOs'
  | 'totalPassageiros'

// Retorno de GET OrdemServico/{osId}/historico-andamento.
export interface HistoricoAndamentoVm {
  id: number
  ordemServicoId: string
  statusAnteriorId: number | null
  statusAnterior: string | null
  statusNovoId: number
  statusNovo: string

  // Só preenchidos quando statusNovoId = 7 (Parada); null nas demais etapas.
  itinerarioId: string | null
  localParada: string | null
  horaParadaPrevista: string | null

  observacao: string | null
  criadoPor: number
  nomeCriadoPor: string

  // Quando o registro entrou no sistema.
  dataCriacao: string
  horaCriacao: string
  // Quando aconteceu de fato — difere do par acima quando o app registrou offline
  // e sincronizou depois.
  dataOcorrido: string
  horaOcorrido: string
}
